import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { StarRating } from "./StarRating";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare } from "lucide-react";

interface ReviewModalProps {
  open: boolean;
  onClose: () => void;
  appointmentId: number;
  professionalId: number;
  serviceId: number;
  serviceName: string;
  professionalName: string;
  salonId?: number;
}

const ratingLabels = ["", "Ruim", "Regular", "Bom", "Ótimo", "Excelente"];

export function ReviewModal({
  open,
  onClose,
  appointmentId,
  professionalId,
  serviceId,
  serviceName,
  professionalName,
  salonId = 1,
}: ReviewModalProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  const utils = trpc.useUtils();
  const createReview = trpc.reviews.create.useMutation({
    onSuccess: () => {
      toast.success("Avaliação enviada! Obrigado pelo seu feedback.");
      utils.appointments.getMyAppointments.invalidate();
      utils.reviews.listPublic.invalidate();
      utils.reviews.getStats.invalidate({ salonId });
      onClose();
      setRating(0);
      setComment("");
    },
    onError: (err) => {
      toast.error(err.message || "Erro ao enviar avaliação.");
    },
  });

  const handleSubmit = () => {
    if (rating === 0) {
      toast.warning("Por favor, selecione uma nota de 1 a 5 estrelas.");
      return;
    }
    createReview.mutate({
      salonId,
      appointmentId,
      professionalId,
      serviceId,
      rating,
      comment: comment.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-[Cormorant_Garamond,serif] text-2xl">
            Avaliar Atendimento
          </DialogTitle>
          <DialogDescription>
            Como foi sua experiência com <strong>{serviceName}</strong> com{" "}
            <strong>{professionalName}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Star rating */}
          <div className="flex flex-col items-center gap-3">
            <StarRating
              rating={rating}
              size="lg"
              interactive
              onRate={setRating}
            />
            {rating > 0 && (
              <span className="text-sm font-medium text-amber-600">
                {ratingLabels[rating]}
              </span>
            )}
          </div>

          {/* Comment */}
          <div className="space-y-1.5">
            <Label htmlFor="review-comment" className="flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              Comentário <span className="text-muted-foreground">(opcional)</span>
            </Label>
            <Textarea
              id="review-comment"
              placeholder="Conte como foi sua experiência..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{comment.length}/1000</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={createReview.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createReview.isPending || rating === 0}
            className="bg-[#8B6914] hover:bg-[#7a5c12] text-white"
          >
            {createReview.isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enviando...</>
            ) : (
              "Enviar Avaliação"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
